import {ReactNode, useEffect, useState} from 'react';

import * as sdk from 'matrix-js-sdk';
import {useMatrixContext} from '@/contexts/matrix/client';
import MatrixUnreadEventsContext from './context';
import {getCurrentRoomId} from '@/services/matrix/unread-events';

type MatrixUnreadEventsProviderProps = {
  children: ReactNode;
  documentId?: string;
  roomId?: string;
};

export const MatrixUnreadEventsProvider: React.FC<
  MatrixUnreadEventsProviderProps
> = ({children, documentId, roomId}) => {
  const [unreadEventsCount, setUnreadEventsCount] = useState(0);

  const [lastReadEvent, setLastReadEvent] = useState<string | null>(null);

  const [currentRoomId, setCurrentRoomId] = useState<string | null>(null);

  const {matrixClient, matrixUserId} = useMatrixContext();

  useEffect(() => {
    if (!matrixClient) {
      return;
    }
    if (roomId) {
      setCurrentRoomId(roomId);
    } else if (documentId) {
      getCurrentRoomId(matrixClient, documentId)
        .then((roomId) => {
          if (roomId) {
            setCurrentRoomId(roomId);
          }
        })
        .catch((error) => {
          console.error('Failed to get current room id: ', error);
        });
    }
  }, [documentId, matrixClient, roomId]);

  useEffect(() => {
    if (!currentRoomId || !matrixClient || !matrixUserId) {
      return;
    }

    const room = matrixClient.getRoom(currentRoomId);
    if (!room) return;

    const isMember = room.getMyMembership() === 'join';
    if (!isMember) return;

    const updateUnreadCount = () => {
      const unreadCount = room.getUnreadNotificationCount(
        sdk.NotificationCountType.Total
      );

      setUnreadEventsCount(unreadCount);

      const lastEventRead = room.getEventReadUpTo(matrixUserId);

      if (lastEventRead) {
        setLastReadEvent(lastEventRead);
      }
    };

    updateUnreadCount();

    const handleReceiptEvent = (
      event: sdk.MatrixEvent,
      eventRoom: sdk.Room
    ) => {
      if (eventRoom.roomId === currentRoomId) updateUnreadCount();
    };

    matrixClient.on(sdk.RoomEvent.Receipt, handleReceiptEvent);

    return () => {
      matrixClient.off(sdk.RoomEvent.Receipt, handleReceiptEvent);
    };
  }, [matrixClient, currentRoomId, matrixUserId]);

  return (
    <MatrixUnreadEventsContext.Provider
      value={{
        unreadEventsCount,
        lastReadEvent
      }}
    >
      {children}
    </MatrixUnreadEventsContext.Provider>
  );
};

export default MatrixUnreadEventsProvider;
